import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import '../../assets/scss/components/_invoice.scss';
import {
  BookingDetailsData,
  BookingInvoiceItem,
  getBookingContainerDetailsAsync,
  getBookingTravelvatsAsync,
  getBookingvatsAsync,
  selectBookingDetails,
  selectBookingDetailsLoadingStatus,
  selectBookingTravelVatReport,
  selectBookingVatReport,
  setBookingContainer,
} from './bookingsSlice';
import {
  getSiteDetailsAsync,
  selectSiteDetails,
} from '../offices/OfficesSlice';

const localizedStrings = {
  en: {
    careTakers: 'Care Takers',
    ownerVats: 'Owner vat details',
    withoutVatTotal: 'Total without VAT',
    vatTotal: 'VAT',
    product: 'Product',
    pricewoVat: 'Price w/o VAT',
    price: 'Price',
    serves: 'Your officer:',
    email: 'Email',
    finalInvoice: 'Final Invoice',
    advanceInvoice: 'Advance Invoice',
    advanceInvoiced: 'Advance Billed',
    compensationInvoice: 'Compensation Invoice',
    date: 'Date',
    invoiceNumber: 'Invoice Number',
    customerNumber: 'Customer Number',
    bookingNumber: 'Booking Number',
    dueDate: 'Due Date',
    dueDateSv: '',
    delayFee: 'Delay Fee',
    total: 'Total',
    includedInPrice: 'Included in Price',
    referenceNumber: 'Reference Number',
    finalInvoiceEUR: 'Final Invoice EUR',
    advanceInvoiceEUR: 'Advance Invoice EUR',
    totalEUR: 'Total EUR',
    additionalInfo: 'Additional Information',
    note: 'Comments',
    travelAgencyCommission: 'Travel agency partner commission by tax category',
    travelAgencyCommissionMj: 'Travel agency commission by tax category',
    net: 'Net',
    vat: 'VAT',
    totalWithVAT: 'Total with VAT',
    vatByRates: 'VAT itemization by category',
    bank: 'BANK',
    recipientAccountNumber: 'Recipient Account Number',
    recipientAccountNumberSv: '',
    recipient: 'Recipient',
    recipientSv: '',
    payer: 'Payer',
    payerSv: '',
    refnro: 'Ref.Nro',
    refnroSv: '',
    signature: 'Signature',
    signatureSv: '',
    fromAccount: 'From Account',
    fromAccountSv: '',
    paymentDueDate: 'Payment Due Date',
    euro: 'EURO',
    serviceName: 'Property / Services',
    unite: 'Units',
    time: 'Period',
    adults: 'Adl',
    chils: 'Chd',
    unitprice: 'Price w/o VAT',
    vatPercent: 'Vat%',
    discount: 'Discount',
    commission: 'Commiss.',
    totalLine: 'Total (incl. VAT)',
    inclVat: 'Incl. VAT',
    netto: 'Netto',
    copy: '(Copy)',
  },
  fi: {
    careTakers: 'Huoltajat',
    ownerVats: 'Omistajien alv-tiedot',
    withoutVatTotal: 'Arvonlisäveroton hinta yhteensä',
    vatTotal: 'Arvonlisävero yhteensä',
    product: 'Tuote',
    pricewoVat: 'Hinta ilman alv',
    price: 'Hinta',
    serves: 'Sinua palveli:',
    email: 'Sähköposti',
    finalInvoice: 'Loppulasku',
    advanceInvoice: 'Ennakkolasku',
    advanceInvoiced: 'Ennakkolaskutettu',
    compensationInvoice: 'Hyvityslasku',
    date: 'Päivämäärä',
    invoiceNumber: 'Laskun numero',
    customerNumber: 'Asiakasnumero',
    bookingNumber: 'Varaustunnus',
    dueDate: 'Eräpäivä',
    dueDateSv: 'Förf.dag',
    delayFee: 'Viivästyskorko',
    total: 'Yhteensä',
    includedInPrice: 'Sisältyy hintaan',
    referenceNumber: 'Viitenumero',
    finalInvoiceEUR: 'Loppulasku EUR',
    advanceInvoiceEUR: 'Ennakkolasku EUR',
    totalEUR: 'Yhteensä EUR',
    additionalInfo: 'Lisätiedot',
    note: 'Tiedote',
    travelAgencyCommission: 'Matkatoimistoprovisio verokannoittain',
    travelAgencyCommissionMj: 'Matkan järjestäjä provisio verokannoittain',
    net: 'Netto',
    vat: 'Alv',
    totalWithVAT: 'Yhteensä',
    vatByRates: 'Arvonlisävero verokannoittain',
    bank: 'PANKKI',
    recipientAccountNumber: 'Saajan tilinumero',
    recipientAccountNumberSv: 'Mottagarensv kontonumme',
    recipient: 'Saaja',
    recipientsv: 'Mottagare',
    payer: 'Maksaja',
    payerSv: 'Betalare',
    refnro: 'Viitenro',
    refnroSv: 'Ref.nr',
    signature: 'Allekirjoitus',
    signatureSv: 'Underskrift',
    fromAccount: 'Tililtä nro',
    fromAccountSv: 'Från konto',
    paymentDueDate: 'Eräpäivä',
    euro: 'EURO',
    serviceName: 'Kohde / palvelut',
    unite: 'Kpl',
    time: 'Aika',
    adults: 'Aik',
    chils: 'Lap',
    unitprice: 'Yksikköhinta',
    vatPercent: 'Alv%',
    discount: 'Alennus',
    commission: 'Provisio',
    totalLine: 'Yhteensä (sis. alv)',
    inclVat: 'Sis. Alv',
    netto: 'Netto',
    copy: '(Kopio)',
  },
};
function BookingCrossInvoiceDetails() {
  const { locale = 'fi' } = useParams();
  const strings = localizedStrings[locale] || localizedStrings.fi;
  const backLinkUrl = '/bookings/search-bookings';
  const params = useParams();
  const state = useLocation();
  const dispatch = useAppDispatch();
  const bookingContainer = useAppSelector(selectBookingDetails);
  const siteDetails = useAppSelector(selectSiteDetails);
  const bookingDetailsLoadingStatus = useAppSelector(
    selectBookingDetailsLoadingStatus,
  );
  const bookingVats = useAppSelector(selectBookingVatReport);
  const bookingTravelVats = useAppSelector(selectBookingTravelVatReport);
  const travelVatLinesFinal = bookingTravelVats?.vatItems
    .filter(i => i.finalNetto > 0)
    .map(item => (
      <tr>
        <td>{item.vatP}</td>
        <td style={{ textAlign: 'right' }}>
          {(item.finalNetto + item.advanceNetto).toFixed(2)}
        </td>
        <td style={{ textAlign: 'right' }}>
          {(item.finalVatAmount + item.advanceVatAmount).toFixed(2)}
        </td>
        <td style={{ textAlign: 'right' }}>
          {(item.finalTotal + item.advanceTotal).toFixed(2)}
        </td>
      </tr>
    ));
  const [invoiceData, setInvoiceData] = useState<BookingInvoiceItem>({
    referenceNumber: 0,
    discriminator: '',
    number: 0,
    invoiceDate: '',
    entryDate: '',
    dueDate: '',
    booking: '',
    delayFeeP: 0,
    compensationInvoiceId: '',
  } as BookingInvoiceItem);
  const [bookingData, setBookingData] = useState<BookingDetailsData>({
    id: '',
    createdAt: '',
    payerId: '',
    authorId: '',
    bookingType: 0,
    bookingNumber: 0,
    payerFirstName: '',
    payerLastName: '',
    payerAccountNumber: '',
    accommodationId: '',
    isCancelled: false,
    bookingOrderItems: [],
    customerBookingNotes: [],
    ownerBookingNotes: [],
    officerBookingNotes: [],
    netPrice: 0,
    finalBillPrice: 0,
    finalBillPriceWithoutVat: 0,
    finalBillPriceVat: 0,
    advanceBillPrice: 0,
    advanceBillPriceVat: 0,
    advanceBillPriceWithoutVat: 0,
    bookingContainerId: '',
    invoicingLanguageLocale: '',
    bookingInvoiceItems: [],
    errors: [],
    webShopCustomer: null,
    isAllowedToCancel: false,
    isAllowedToEdit: false,
    isAddOnBooking: false,
    reasonOfCancellation: '',
    extraBookingResponses: [],
    preliminaryBookingDueDate: null,
  } as BookingDetailsData);
  const initFetch = useCallback(() => {
    // console.debug('initFetch', locale, params);
    dispatch(getBookingvatsAsync(params.bookingId));
    dispatch(getBookingTravelvatsAsync(params.bookingId));
    if (params.bookingId !== 'search') {
      dispatch(getBookingContainerDetailsAsync(params.bookingId)).then(res => {
        if (res.payload === null) {
          dispatch(setBookingContainer(null));
        }
      });
    }
  }, [dispatch, params.bookingId]);
  useEffect(() => {
    initFetch();
  }, [initFetch, params.bookingId]);
  useEffect(() => {
    if (bookingContainer && bookingContainer.bookingResponses) {
      const bookingDataFound = bookingContainer.bookingResponses.filter(br =>
        br.bookingInvoiceItems.filter(
          i => i.number.toString() === params.invoiceId,
        ),
      );
      if (bookingDataFound && bookingDataFound.length > 0) {
        setBookingData(bookingDataFound[0]);
        const invoice = bookingDataFound[0].bookingInvoiceItems.filter(
          i => i.number.toString() === params.invoiceId,
        );
        if (invoice && invoice.length > 0) {
          setInvoiceData(invoice[0]);
          dispatch(getSiteDetailsAsync(invoice[0].siteId));
        }
      }
    }
  }, [bookingContainer]);
  useEffect(() => {
    // console.debug('siteDetails', siteDetails);
  }, [siteDetails]);
  const getInvoiceTitle = () => {
    if (invoiceData && invoiceData.discriminator.includes('FinalInvoice')) {
      return `${strings.finalInvoice}${params.copy === 'true' ? `${strings.copy}` : ''}`;
    }
    if (invoiceData && invoiceData.discriminator.includes('AdvanceInvoice')) {
      return `${strings.advanceInvoice}${params.copy === 'true' ? `${strings.copy}` : ''}`;
    }
    if (
      invoiceData &&
      invoiceData.discriminator.includes('CompensationInvoice')
    ) {
      return 'HYVIITYSLASKU';
    }
    return '';
  };
  const getIsAdvance = () => {
    if (invoiceData && invoiceData.discriminator.includes('FinalInvoice')) {
      return false;
    }
    if (invoiceData && invoiceData.discriminator.includes('AdvanceInvoice')) {
      return true;
    }
    if (
      invoiceData &&
      invoiceData.discriminator.includes('CompensationInvoice')
    ) {
      return false;
    }
    return false;
  };

  const getIsFinal = () => {
    if (invoiceData && invoiceData.discriminator.includes('FinalInvoice')) {
      return true;
    }
    if (invoiceData && invoiceData.discriminator.includes('AdvanceInvoice')) {
      return false;
    }
    if (
      invoiceData &&
      invoiceData.discriminator.includes('CompensationInvoice')
    ) {
      return false;
    }
    return false;
  };
  const linesCareTaker = bookingVats?.careTakerVats?.map(item => (
    <tbody style={{ borderTop: 'solid' }}>
      <tr>
        <td>{item.ownerNameNumber}</td>
      </tr>
      <tr>
        <td>{item.ownerPhone}</td>
      </tr>
      <tr>
        <td>{item.ownerEmail}</td>
      </tr>
    </tbody>
  ));
  const getInvoiceIncludedWithoutVat = () => {
    let sum = 0;
    bookingData?.bookingOrderItems
      .filter(inv => inv.includedInPrice)
      .map(item => {
        sum += item.pricesResponse.finalBillPriceWithoutVat;
        return sum;
      });
    return sum.toFixed(2);
  };
  const getInvoiceIncluded = () => {
    let sum = 0;
    bookingData?.bookingOrderItems
      .filter(inv => inv.includedInPrice)
      .map(item => {
        sum += item.pricesResponse.finalBillPrice;
        return sum;
      });
    return sum.toFixed(2);
  };

  const getInvoiceIncludedVat = () => {
    let sum = 0;
    bookingData?.bookingOrderItems
      .filter(inv => inv.includedInPrice)
      .map(item => {
        sum += item.pricesResponse.finalBillPriceVat;
        return sum;
      });
    return sum;
  };

  const getWithOutVatFinal = () => {
    let sum = 0;
    bookingData?.bookingOrderItems
      .filter(
        line =>
          !line.itemName.toLowerCase().includes('toimitus') &&
          !line.itemName.toLowerCase().includes('peruutus'),
      )
      .map(item => {
        sum +=
          item.purchasePricesResponse.sitePurchasePrice -
          item.purchasePricesResponse.sitePurchasePriceVat;
        return sum;
      });
    return sum.toFixed(2);
  };
  const getWithOutVatAdvance = () => {
    let sum = 0;
    bookingData?.bookingOrderItems.map(item => {
      sum += item.pricesResponse.advanceBillPriceWithoutVat;
      return sum.toFixed(2);
    });
    return sum.toFixed(2);
  };
  const getVatFinal = () => {
    let sum = 0;
    bookingData?.bookingOrderItems.map(item => {
      sum += item.purchasePricesResponse.sitePurchasePriceVat;
      return sum;
    });
    return sum.toFixed(2);
  };
  const getVatAdvance = () => {
    let sum = 0;
    bookingData?.bookingOrderItems.map(item => {
      sum += item.pricesResponse.advanceBillPriceVat;
      return sum;
    });
    return sum.toFixed(2);
  };
  const advanceIvoiceLines = bookingData?.bookingOrderItems
    .filter(line => line.pricesResponse.advanceBillPrice > 0)
    .map(item => (
      <tr>
        <td>{item.itemName}</td>
        <td style={{ textAlign: 'center' }}>{item.units}</td>
        <td>
          {new Date(Date.parse(item.startDateTime)).toLocaleDateString('fi-FI')}{' '}
          - {new Date(Date.parse(item.endDateTime)).toLocaleDateString('fi-FI')}
        </td>
        <td style={{ textAlign: 'center' }}>{item.amountOfAdults}</td>
        <td style={{ textAlign: 'center' }}>{item.amountOfChildren}</td>
        <td style={{ textAlign: 'right' }}>
          {item.orderItemType === 0
            ? item.pricesResponse.advanceBillPriceWithoutVat
            : item.pricesResponse.advanceBillPriceWithoutVat}
        </td>
        <td style={{ textAlign: 'right' }}>
          {item.pricesResponse.advanceBillPriceVat}
        </td>
        <td style={{ textAlign: 'right' }}>{item.pricesResponse.vatP}%</td>
        <td style={{ textAlign: 'right' }}>{item.pricesResponse.discount}</td>
        <td style={{ textAlign: 'right' }}>{item.pricesResponse.comission}</td>
        <td style={{ textAlign: 'right' }}>
          {item.pricesResponse.advanceBillPrice}
        </td>
      </tr>
    ));
  const vatLinesFinal = bookingVats?.vatItems.map(item => (
    <tr>
      <td>{item.vatP}</td>
      <td style={{ textAlign: 'right' }}>{item.finalNetto}</td>
      <td style={{ textAlign: 'right' }}>{item.finalVatAmount}</td>
      <td style={{ textAlign: 'right' }}>{item.finalTotal}</td>
    </tr>
  ));

  const notesLines = bookingData?.customerBookingNotes?.map(item => (
    <tr>
      <td>{item.noteText}</td>
    </tr>
  ));

  const getTotal = () => {
    let sum = 0;
    bookingVats?.vatItems.map(item => {
      sum += item.finalTotal;
      return sum;
    });
    return sum.toFixed(2);
  };
  const vatLinesAdvance = bookingVats?.vatItems.map(item => (
    <tr>
      <td>{item.vatP}</td>
      <td style={{ textAlign: 'right' }}>{item.advanceNetto}</td>
      <td style={{ textAlign: 'right' }}>{item.advanceVatAmount}</td>
      <td style={{ textAlign: 'right' }}>{item.advanceTotal}</td>
    </tr>
  ));
  // console.debug('vatLinesAdvance', bookingVats.ownerVats);
  const vatLinesOwner = bookingVats?.ownerVats?.map(item => (
    <tbody style={{ borderTop: 'solid' }}>
      <tr>
        <td>{item.ownerNameNumber}</td>
      </tr>
      <tr>
        <td>{item.ownerPhone}</td>
      </tr>
      <tr>
        <td>{item.ownerEmail}</td>
      </tr>
      <table>
        <tr>
          <th style={{ textAlign: 'left' }}>
            <strong>{strings.product}</strong>
          </th>
          <th style={{ textAlign: 'right' }}>
            <strong>{strings.vatPercent}</strong>
          </th>
          <th style={{ textAlign: 'right' }}>
            <strong>{strings.pricewoVat}</strong>
          </th>
          <th style={{ textAlign: 'right' }}>
            <strong>{strings.vat}</strong>
          </th>
          <th style={{ textAlign: 'right' }}>
            <strong>{strings.totalWithVAT}</strong>
          </th>
        </tr>
        {item?.ownerVatItems?.map(vat => (
          <tbody>
            <tr>
              <td style={{ textAlign: 'left', width: '20%' }}>
                {vat.itemNameNumber}
              </td>
              <td style={{ textAlign: 'right' }}>{vat.vatP}</td>
              <td style={{ textAlign: 'right' }}>{vat.netto}</td>
              <td style={{ textAlign: 'right' }}>{vat.vatAmount}</td>
              <td style={{ textAlign: 'right' }}>{vat.total}</td>
            </tr>
          </tbody>
        ))}
      </table>
    </tbody>
  ));
  const ibanLines = siteDetails?.siteBankData.map(item => (
    <div>
      {item.iban}
      <br />
    </div>
  ));
  const bicLines = siteDetails?.siteBankData.map(item => (
    <tr>
      <td>{item.bic}</td>
    </tr>
  ));
  const finalInvoiceLines = bookingData?.bookingOrderItems
    .filter(line => !line.itemName.toLowerCase().includes('toimitus'))
    .map(item => (
      <tr>
        <td>{item.itemName}</td>
        {!item.includedInPrice && (
          <>
            <td style={{ textAlign: 'center' }}>{item.units}</td>
            <td>
              {new Date(Date.parse(item.startDateTime)).toLocaleDateString(
                'fi-FI',
              )}{' '}
              -{' '}
              {new Date(Date.parse(item.endDateTime)).toLocaleDateString(
                'fi-FI',
              )}
            </td>
            <td style={{ textAlign: 'center' }}>{item.amountOfAdults}</td>
            <td style={{ textAlign: 'center' }}>{item.amountOfChildren}</td>
            <td style={{ textAlign: 'right' }}>
              {(
                item.purchasePricesResponse.sitePurchasePrice -
                item.purchasePricesResponse.sitePurchasePriceVat
              ).toFixed(2)}
            </td>
            <td style={{ textAlign: 'right' }}>
              {item.purchasePricesResponse.sitePurchasePriceVat}
            </td>
            <td style={{ textAlign: 'right' }}>{item.pricesResponse.vatP}%</td>
            <td style={{ textAlign: 'right' }}>
              {item.pricesResponse.discount}
            </td>
            <td style={{ textAlign: 'right' }}>
              {item.pricesResponse.travelAgencyComission}
            </td>
            <td style={{ textAlign: 'right' }}>
              {item.purchasePricesResponse.sitePurchasePrice}
            </td>
          </>
        )}
        {item.includedInPrice && (
          <td colSpan={2} style={{ textAlign: 'center' }}>
            {strings.includedInPrice}
          </td>
        )}
      </tr>
    ));
  return (
    <div className="invoice-body">
      <Helmet>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>{getInvoiceTitle()}</title>
      </Helmet>
      <div>
        <div className="invoice-container">
          <header className="header">
            <table style={{ width: '100%' }}>
              <thead style={{ display: 'table-header-group' }}>
                <tr>
                  <td style={{ width: '50%' }}>
                    <div className="logo-section">
                      <p className="large">
                        <strong>{siteDetails?.name}</strong>
                        <br />
                        {siteDetails?.postStreetName}
                        {', '}
                        {siteDetails?.postPostCode} {siteDetails?.postCity}
                        <br />
                        {`Sinua palveli: ${bookingData.authorFirstName} ${bookingData.authorLastName}`}
                        <br />
                        {`Sähköposti: ${bookingData.authorEmail ?? ''}`}
                        <br />
                      </p>
                    </div>
                    <div
                      className="recipient-info"
                      style={{ paddingTop: '10px', paddingBottom: '20px' }}
                    >
                      <p>
                        <p>
                          {siteDetails?.siteCustomerName ?? ''} <br />
                          {siteDetails?.siteCustomerAddress ?? ''}
                          <br />
                          {siteDetails?.siteCustomerPostAddress ?? ''}
                        </p>
                      </p>
                    </div>
                  </td>
                  <td style={{ width: '50%' }} valign="top">
                    <div className="invoice-details">
                      <span
                        className="large"
                        style={{
                          display: 'inline-block',
                          paddingBottom: '20px',
                        }}
                      >
                        <strong>{getInvoiceTitle()}</strong>
                      </span>
                      <table>
                        <tbody>
                          <tr>
                            <td className="label" style={{ width: '100px' }}>
                              {strings.date}:
                            </td>
                            <td>
                              {new Date(
                                Date.parse(invoiceData.entryDate),
                              ).toLocaleDateString('fi-FI')}
                            </td>
                          </tr>
                          <tr>
                            <td className="label">{strings.invoiceNumber}:</td>
                            <td>{invoiceData.number}</td>
                          </tr>
                          <tr>
                            <td className="label">{strings.customerNumber}:</td>
                            <td>{bookingData.payerAccountNumber}</td>
                          </tr>
                          <tr>
                            <td className="label">{strings.bookingNumber}:</td>
                            <td>{bookingData.bookingNumber}</td>
                          </tr>
                          <tr>
                            <td
                              className="label"
                              style={{ paddingTop: '20px' }}
                            >
                              <strong>{strings.dueDate}:</strong>
                            </td>
                            <td style={{ paddingTop: '20px' }}>
                              <strong>
                                {new Date(
                                  Date.parse(invoiceData.dueDate),
                                ).toLocaleDateString('fi-FI')}
                              </strong>
                            </td>
                          </tr>
                          <tr>
                            <td className="label">{strings.delayFee}:</td>
                            <td>{invoiceData.delayFeeP}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </thead>
              <tbody />
            </table>
          </header>
          {/* Invoice Table */}
          <section className="invoice-table">
            <table>
              <thead className="small">
                <tr>
                  <th style={{ width: '220px' }}>{strings.serviceName}</th>
                  <th style={{ textAlign: 'center' }}>{strings.unite}</th>
                  <th style={{ width: '220px' }}>{strings.time}</th>
                  <th style={{ textAlign: 'center' }}>{strings.adults}</th>
                  <th style={{ textAlign: 'center' }}>{strings.chils}</th>
                  <th style={{ textAlign: 'right' }}>{strings.unitprice}</th>
                  <th style={{ textAlign: 'right' }}>{strings.vat}</th>
                  <th style={{ textAlign: 'right' }}>{strings.vatPercent}</th>
                  <th style={{ textAlign: 'right' }}>{strings.discount}</th>
                  <th style={{ textAlign: 'right' }}>{strings.commission}</th>
                  <th style={{ textAlign: 'right', width: '110px' }}>
                    {strings.totalLine}
                  </th>
                </tr>
              </thead>
              <tbody
                style={{
                  borderBottom: 'solid 1px #000000',
                  borderTop: 'solid 1px #000000',
                }}
              >
                {getIsAdvance() && advanceIvoiceLines}
                {getIsFinal() && finalInvoiceLines}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={9} style={{ width: '100%' }} />
                  <td>
                    <strong>{strings.total}</strong>
                  </td>
                  <td style={{ width: '120px', textAlign: 'right' }}>
                    <strong>{getIsFinal() && getTotal()}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>
          {/* Tax */}
          <section className="taxes">
            <table>
              <tbody>
                <tr>
                  <td style={{ width: '48%' }}>
                    {' '}
                    {notesLines.length > 0 && (
                      <table
                        style={{
                          width: '100%',
                          marginLeft: '0px',
                          marginBottom: '10px',
                          marginTop: '10px',
                          border: 'solid 1px #000000',
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              width: '200px',
                              textAlign: 'left',
                              border: 'solid 1px #000000',
                            }}
                          >
                            <th colSpan={4}>
                              <strong>{strings.additionalInfo}</strong>
                            </th>
                          </tr>
                          <tr>
                            <th style={{ width: '200px', textAlign: 'left' }}>
                              <strong>{strings.note}:</strong>
                            </th>
                          </tr>
                        </thead>
                        <tbody>{notesLines}</tbody>
                      </table>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ width: '48%' }} />
                  <td style={{ width: '4%' }} />
                  <td style={{ width: '48%' }}>
                    <table
                      style={{
                        marginLeft: 'auto',
                        marginBottom: '20px',
                        marginTop: '40px',
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ width: '200px', textAlign: 'right' }}>
                            <strong />
                          </th>
                          <th style={{ width: '100px', textAlign: 'right' }}>
                            {strings.withoutVatTotal}
                          </th>
                          <th style={{ width: '100px', textAlign: 'right' }}>
                            {strings.vatTotal}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td />
                          <td style={{ textAlign: 'right' }}>
                            {getIsFinal() && getWithOutVatFinal()}
                            {getIsAdvance() && getWithOutVatAdvance()}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {getIsFinal() && getVatFinal()}
                            {getIsAdvance() && getVatAdvance()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <table
                      style={{
                        marginLeft: 'auto',
                        marginBottom: '20px',
                        marginTop: '40px',
                        border: 'solid 1px #000000',
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            width: '200px',
                            textAlign: 'left',
                            border: 'solid 1px #000000',
                          }}
                        >
                          <th colSpan={4}>
                            <strong>{strings.vatByRates}</strong>
                          </th>
                        </tr>
                        <tr>
                          <th style={{ width: '200px', textAlign: 'left' }}>
                            <strong>{strings.inclVat}</strong>
                          </th>
                          <th style={{ width: '100px', textAlign: 'right' }}>
                            {strings.netto}
                          </th>
                          <th style={{ width: '100px', textAlign: 'right' }}>
                            {strings.vat}
                          </th>
                          <th style={{ width: '100px', textAlign: 'right' }}>
                            {strings.total}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getIsFinal() && vatLinesFinal}
                        {getIsAdvance() && vatLinesAdvance}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
          {/* Payment Info */}
          {(params.copy === 'false' || getIsFinal()) && (
            <>
              <table>
                <tbody>
                  <tr style={{ border: 'solid' }}>
                    <th style={{ border: 'solid' }}>
                      <span
                        style={{
                          display: 'block',
                          textAlign: 'left',
                          verticalAlign: 'top',
                        }}
                      >
                        Viitenumero
                      </span>
                      <span
                        style={{
                          display: 'block',
                          textAlign: 'right',
                          verticalAlign: 'center',
                        }}
                      >
                        {invoiceData.referenceNumber}
                      </span>
                    </th>
                    <th style={{ border: 'solid' }}>
                      {getIsFinal() && 'Loppulasku EUR'}
                    </th>
                    <th style={{ border: 'solid' }}>
                      <strong>{getIsFinal() && getTotal()}</strong>
                    </th>
                  </tr>
                </tbody>
              </table>
              <table>
                <tbody>
                  <tr>
                    <th style={{ width: '66%' }} />
                    <th style={{ width: '100%' }}>
                      <strong>Yhteensä EUR</strong>
                    </th>
                    <th style={{ width: '100%', textAlign: 'left' }}>
                      <strong>{getTotal()}</strong>
                    </th>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          <section className="payment-info" style={{ marginTop: '40px' }}>
            {params.copy === 'false' && (
              <>
                <span className="tiny">
                  <span className="tiny">
                    {siteDetails?.name ?? ''} |{' '}
                    {siteDetails?.postStreetName ?? ''},{' '}
                    {siteDetails?.postPostCode ?? ''}{' '}
                    {siteDetails?.postCity ?? ''} | {siteDetails?.email} |{' '}
                    {siteDetails?.phone} | Y-tunnus: {siteDetails?.ycode}
                  </span>
                </span>
                <table
                  style={{
                    marginTop: '8px',
                    borderBottom: 'solid 1px #000000',
                    borderTop: 'dashed 2px #000000',
                  }}
                >
                  <tbody>
                    <tr style={{ borderBottom: 'solid 1px #000000' }}>
                      <td
                        className="tiny"
                        style={{
                          width: '60px',
                          borderRight: 'solid 1px #000000',
                        }}
                      >
                        <strong>
                          Saajan tilinumero
                          <br />
                          Mottagarensv kontonumme
                        </strong>
                      </td>
                      <td style={{ borderRight: 'solid 1px #000000' }}>
                        <strong className="tiny">IBAN</strong>
                        {ibanLines}
                      </td>
                      <td colSpan={4} style={{ width: '45%' }}>
                        <strong className="tiny">BIC</strong>
                        {bicLines}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="tiny"
                        style={{
                          width: '60px',
                          borderRight: 'solid 1px #000000',
                          borderBottom: 'solid 1px #000000',
                        }}
                      >
                        <strong>
                          Saaja
                          <br />
                          Mottagare
                        </strong>
                      </td>
                      <td
                        style={{
                          borderRight: 'solid 1px #000000',
                          borderBottom: 'solid 1px #000000',
                        }}
                      >
                        {siteDetails?.name}
                      </td>
                      <td
                        rowSpan={2}
                        colSpan={4}
                        style={{ borderBottom: 'solid 1px #000000' }}
                      >
                        Laskun numero:{invoiceData.number}
                        <br />
                        Asiakasnumero:{bookingData.payerAccountNumber}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: 'solid 1px #000000' }}>
                      <td
                        className="tiny"
                        style={{
                          width: '60px',
                          borderRight: 'solid 1px #000000',
                        }}
                      >
                        <strong>
                          Maksaja
                          <br />
                          Betalare
                        </strong>
                      </td>
                      <td style={{ borderRight: 'solid 1px #000000' }}>
                        <p>
                          {siteDetails?.siteCustomerName ?? ''} <br />
                          {siteDetails?.siteCustomerAddress ?? ''}
                          <br />
                          {siteDetails?.siteCustomerPostAddress ?? ''}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="tiny"
                        style={{
                          width: '60px',
                          borderBottom: 'solid 1px #000000',
                        }}
                      >
                        <strong>
                          Allekirjoitus
                          <br />
                          Underskrift
                        </strong>
                      </td>
                      <td
                        style={{
                          borderRight: 'solid 1px #000000',
                          borderBottom: 'solid 1px #000000',
                        }}
                      >
                        <div
                          style={{
                            borderBottom: 'solid 1px #000000',
                            height: '20px',
                          }}
                        />
                      </td>
                      <td
                        className="tiny"
                        style={{
                          width: '60px',
                          borderRight: 'solid 1px #000000',
                          borderBottom: 'solid 1px #000000',
                        }}
                      >
                        <strong>
                          Viitenro
                          <br />
                          Ref.nr
                        </strong>
                      </td>
                      <td
                        colSpan={3}
                        style={{ borderBottom: 'solid 1px #000000' }}
                      >
                        {invoiceData.referenceNumber}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="tiny"
                        style={{
                          width: '60px',
                          borderBottom: 'solid 1px #000000',
                        }}
                      >
                        <strong>
                          Tililtä nro
                          <br />
                          Från konto
                        </strong>
                      </td>
                      <td
                        style={{
                          borderRight: 'solid 1px #000000',
                          borderBottom: 'solid 1px #000000',
                        }}
                      />
                      <td
                        className="tiny"
                        style={{
                          width: '60px',
                          borderRight: 'solid 1px #000000',
                        }}
                      >
                        <strong>
                          Eräpäivä
                          <br />
                          Förf.dag
                        </strong>
                      </td>
                      <td style={{ borderRight: 'solid 1px #000000' }}>
                        {new Date(
                          Date.parse(invoiceData.dueDate),
                        ).toLocaleDateString('fi-FI')}
                      </td>
                      <td className="tiny" style={{ width: '60px' }}>
                        <strong>EURO</strong>
                      </td>
                      <td>
                        <strong>
                          {getIsFinal() && bookingData.finalBillPrice}
                          {getIsAdvance() && bookingData.advanceBillPrice}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ textAlign: 'right', marginTop: '8px' }}>
                  <strong>PANKKI-BANKEN</strong>
                </div>
              </>
            )}
            {params.copy === 'true' &&
              vatLinesOwner &&
              vatLinesOwner.length > 0 && (
                <>
                  <div style={{ textAlign: 'left', marginTop: '8px' }}>
                    <strong>{strings.ownerVats}</strong>
                  </div>
                  <table style={{ borderTop: 'solid', borderBottom: 'solid' }}>
                    {params.copy === 'true' && vatLinesOwner}
                  </table>
                </>
              )}
            {params.copy === 'true' &&
              linesCareTaker &&
              linesCareTaker.length > 0 && (
                <>
                  <div style={{ textAlign: 'left', marginTop: '8px' }}>
                    <strong>{strings.careTakers}</strong>
                  </div>
                  <table style={{ borderTop: 'solid', borderBottom: 'solid' }}>
                    {params.copy === 'true' && linesCareTaker}
                  </table>
                </>
              )}
          </section>
        </div>
      </div>
    </div>
  );
}
export default BookingCrossInvoiceDetails;
